<template>
  <div class="seal-test-container">
    <van-nav-bar title="印章相似度测试(通用版)" left-arrow @click-left="$router.back()" />

    <div class="content">
      <!-- 规则选择 -->
      <div class="form-group">
        <label>选择规则</label>
        <el-select v-model="selectedRuleId" placeholder="请选择印章规则" @change="onRuleChange">
          <el-option
            v-for="rule in allRules"
            :key="rule.ruleCode"
            :label="rule.remark"
            :value="rule.ruleCode"
          />
        </el-select>
      </div>

      <!-- 调用门店编码（持久化到本地） -->
      <div class="form-group">
        <label>门店编码</label>
        <el-input
          v-model="authStoreCode"
          placeholder="请输入门店编码"
          clearable
          @input="saveAuthStoreCodeToCache"
        />
      </div>

      <!-- 印章绘制区域：与 sealTest 一致，每次触摸先清空再记录当次触点；并支持 pointer/click 兜底 -->
      <div class="canvas-container">
        <canvas
          ref="canvas"
          class="seal-canvas"
          width="400"
          height="400"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
          @pointerdown="handlePointerDown"
          @click="handleClick"
        />
        <p class="hint">触摸屏幕记录触点位置（4个触点将进行相似度对比）</p>
      </div>

      <!-- 点位显示 -->
      <div v-if="touchPoints.length > 0" class="points-info">
        <p><strong>已绘制点位 ({{ touchPoints.length }}):</strong></p>
        <p v-for="(point, idx) in touchPoints" :key="idx">
          点{{ idx + 1 }}: [{{ point[0] }}, {{ point[1] }}]
        </p>
      </div>

      <!-- 操作按钮 -->
      <div class="button-group">
        <van-button
          type="primary"
          @click="compareSeals"
          :disabled="touchPoints.length === 0 || !currentRule"
        >
          {{ touchPoints.length >= 4 ? '对比相似度' : '记录数据' }}
        </van-button>
        <van-button @click="clearCanvas">清空画布</van-button>
      </div>

      <!-- 对比结果弹窗 -->
      <el-dialog
        title="对比结果"
        :visible.sync="showResultDialog"
        width="90%"
        @close="handleDialogClose"
      >
        <div v-if="comparisonResult" class="result-dialog-content">
          <div class="result-item" v-if="comparisonResult.currentPoint !== undefined">
            <span class="label">当前得分:</span>
            <span class="value">{{ comparisonResult.currentPoint }}分</span>
          </div>
          <div class="result-item" v-if="comparisonResult.templatePoints">
            <span class="label">标准点位:</span>
            <span class="value">{{ JSON.stringify(comparisonResult.templatePoints) }}</span>
          </div>
          <div class="result-item" v-if="comparisonResult.currentPoints">
            <span class="label">当前点位:</span>
            <span class="value">{{ JSON.stringify(comparisonResult.currentPoints) }}</span>
          </div>
          <div
            v-if="comparisonResult.valid !== undefined || comparisonResult.matched !== undefined"
            class="result-item"
          >
            <span class="label">匹配结果:</span>
            <span :class="(comparisonResult.valid || comparisonResult.matched) ? 'matched' : 'not-matched'">
              {{ (comparisonResult.valid || comparisonResult.matched) ? '✓ 匹配成功' : '✗ 匹配失败' }}
            </span>
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showResultDialog = false">关闭</el-button>
        </span>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { getSealRules, compareSealPoints } from '@/api/sealTest'

/** 防抖：触摸结束后延迟调用接口的毫秒数（与 sealTest 一致） */
const COMPARE_DEBOUNCE_MS = 1000

/**
 * sealTestCommon 相对 sealTest 的兼容性设计（兼容所有安卓 / iOS）：
 *
 * 1. 事件层：三重采集
 *    - sealTest：仅 touchstart + touchend，依赖 e.touches 多指。
 *    - sealTestCommon：touchstart/touchend（主路径） + pointerdown + click（兜底）。
 *    - 部分安卓 WebView / 旧版 iOS 多指不全或只发 click，用 pointer/click 仍能记到一个点。
 *
 * 2. 画布防误触（仅 Common）
 *    - preventCanvasDrag()：禁止右键菜单、contextmenu。
 *    - CSS touch-action: none：禁止浏览器双指缩放/拖动，避免和触摸采集冲突。
 *    - -webkit-touch-callout / user-select: none：禁止长按选中等，减少误触。
 *
 * 3. Pointer 事件（仅 Common）
 *    - 现代安卓 / iOS 支持 Pointer Events，触摸、手写笔、鼠标统一为 pointerdown。
 *    - 当 touch 未触发或只触发一次时，pointerdown 仍可收到，保证“至少能记一个点”。
 *
 * 4. Click 兜底（仅 Common）
 *    - 仅在鼠标（pointerType === 'mouse'）或环境不支持 PointerEvent 时处理 click。
 *    - 避免移动端 300ms 延迟 click 与 touch 重复；桌面无 touch 时用 click 可正常使用。
 *
 * 5. 主路径逻辑一致
 *    - 触摸主路径与 sealTest 相同：每次 touchstart 先清空再记录当次 e.touches，touchend 防抖/立即 compareSeals。
 *    - 保证在“多指正常”的机型上行为与 sealTest 一致。
 */
export default {
  name: 'SealTestCommon',
  data() {
    return {
      ruleList: [],
      allRules: [],
      selectedRuleId: null,
      currentRule: null,
      showRuleList: false,
      authStoreCode: '',
      touchPoints: [],
      comparisonResult: null,
      showResultDialog: false,
      canvas: null,
      ctx: null,
      standardWidth: 800,
      standardHeight: 600,
      debounceTimer: null,
      /** 避免 touch 与 pointer 重复：touch 后短时间内的 touch 型 pointer 不再处理 */
      _lastTouchTime: 0
    }
  },
  mounted() {
    this.canvas = this.$refs.canvas
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d')
      this.preventCanvasDrag()
    }
    this.authStoreCode =
      (typeof localStorage !== 'undefined' && localStorage.getItem('sealTestCommon_authStoreCode')) || ''
    this.loadRules()
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  },
  methods: {
    /** 禁止画布被拖动/选中，避免误触 */
    preventCanvasDrag() {
      if (!this.canvas) return
      this.canvas.addEventListener('contextmenu', e => e.preventDefault())
      this.canvas.style.touchAction = 'none'
      this.canvas.style.webkitUserSelect = 'none'
      this.canvas.style.userSelect = 'none'
    },
    async loadRules() {
      try {
        const res = await getSealRules()
        const rules = res.data || res
        this.ruleList = rules.map(rule => ({ text: rule.ruleCode, value: rule.id }))
        this.allRules = rules
      } catch (err) {
        console.error('加载规则失败:', err)
        this.$toast.fail('加载规则失败')
      }
    },
    onRuleChange(ruleCode) {
      this.currentRule = this.allRules.find(r => r.ruleCode === ruleCode)
    },
    saveAuthStoreCodeToCache() {
      if (typeof localStorage === 'undefined') return
      const val = (this.authStoreCode && this.authStoreCode.trim()) || ''
      if (val) localStorage.setItem('sealTestCommon_authStoreCode', val)
      else localStorage.removeItem('sealTestCommon_authStoreCode')
    },
    /**
     * 与 sealTest 一致：每次 touchstart 先清空画布和点位，再记录当次所有触点
     */
    handleTouchStart(e) {
      e.preventDefault()
      if (!this.ctx || !this.canvas || !e.touches || e.touches.length === 0) return
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.touchPoints = []

      const rect = this.canvas.getBoundingClientRect()
      const scaleX = this.canvas.width / rect.width
      const scaleY = this.canvas.height / rect.height
      for (let i = 0; i < e.touches.length; i++) {
        const touch = e.touches[i]
        const x = touch.clientX - rect.left
        const y = touch.clientY - rect.top
        const canvasX = x * scaleX
        const canvasY = y * scaleY
        const standardX = Math.round((x / rect.width) * this.standardWidth)
        const standardY = Math.round((y / rect.height) * this.standardHeight)
        this.touchPoints.push([standardX, standardY])
        this.drawPoint(canvasX, canvasY, i + 1)
      }

      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.compareSeals()
        this.debounceTimer = null
      }, COMPARE_DEBOUNCE_MS)
    },
    handleTouchEnd(e) {
      if (e.touches.length === 0) {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer)
          this.debounceTimer = null
          if (this.touchPoints.length > 0) this.compareSeals()
        }
      }
    },
    /** Pointer 兜底：先清空再记录当前一个点（与 sealTest 的“下次按清空”一致） */
    handlePointerDown(e) {
      e.preventDefault()
      if (!this.ctx || !this.canvas) return
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.touchPoints = []
      const rect = this.canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return
      const scaleX = this.canvas.width / rect.width
      const scaleY = this.canvas.height / rect.height
      const canvasX = x * scaleX
      const canvasY = y * scaleY
      const standardX = Math.round((x / rect.width) * this.standardWidth)
      const standardY = Math.round((y / rect.height) * this.standardHeight)
      this.touchPoints.push([standardX, standardY])
      this.drawPoint(canvasX, canvasY, 1)
    },
    /** Click 兜底：桌面鼠标，先清空再记录一个点 */
    handleClick(e) {
      if (!this.ctx || !this.canvas) return
      const isMouse = typeof e.pointerType === 'string' && e.pointerType === 'mouse'
      const noPointer = typeof window === 'undefined' || !window.PointerEvent
      if (!isMouse && noPointer === false) return
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.touchPoints = []
      const rect = this.canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return
      const scaleX = this.canvas.width / rect.width
      const scaleY = this.canvas.height / rect.height
      const canvasX = x * scaleX
      const canvasY = y * scaleY
      const standardX = Math.round((x / rect.width) * this.standardWidth)
      const standardY = Math.round((y / rect.height) * this.standardHeight)
      this.touchPoints.push([standardX, standardY])
      this.drawPoint(canvasX, canvasY, 1)
    },
    drawPoint(x, y, index) {
      this.ctx.fillStyle = '#FF0000'
      this.ctx.beginPath()
      this.ctx.arc(x, y, 8, 0, 2 * Math.PI)
      this.ctx.fill()
      this.ctx.fillStyle = '#FFFFFF'
      this.ctx.font = 'bold 12px Arial'
      this.ctx.textAlign = 'center'
      this.ctx.textBaseline = 'middle'
      this.ctx.fillText(String(index), x, y)
    },
    clearCanvas() {
      if (this.ctx && this.canvas) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      }
      this.touchPoints = []
      this.comparisonResult = null
    },
    async compareSeals() {
      if (this.touchPoints.length === 0 || !this.currentRule) {
        this.$toast.fail('请选择规则并点击/触摸画布记录点位')
        return
      }

      try {
        const res = await compareSealPoints({
          ruleCode: this.currentRule.ruleCode,
          touchPoints: this.touchPoints,
          authStoreCode: (this.authStoreCode && this.authStoreCode.trim()) || undefined
        })
        if (res && res.data) {
          this.comparisonResult = res.data
        } else if (res && (res.valid !== undefined || res.matched !== undefined)) {
          this.comparisonResult = res
        } else {
          this.comparisonResult = res
        }
        if (this.touchPoints.length >= 4) {
          this.showResultDialog = true
        } else {
          this.$toast.success(`已记录 ${this.touchPoints.length} 个触点数据`)
        }
      } catch (err) {
        console.error('接口调用失败:', err)
        this.$toast.fail('操作失败: ' + (err.message || ''))
      }
    },
    handleDialogClose() {
      this.showResultDialog = false
      this.clearCanvas()
    }
  }
}
</script>

<style scoped>
.seal-test-container {
  background: #f5f5f5;
  min-height: 100vh;
}

.content {
  padding: 16px;
}

.form-group {
  background: white;
  padding: 16px;
  margin: 12px 0;
  border-radius: 4px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  font-size: 14px;
  color: #333;
}

.form-group .el-select {
  width: 100%;
}

.rule-info,
.points-info,
.result-info {
  background: white;
  padding: 12px;
  margin: 12px 0;
  border-radius: 4px;
  font-size: 14px;
}

.canvas-container {
  background: white;
  margin: 12px 0;
  border-radius: 4px;
  text-align: center;
  padding: 12px;
}

.seal-canvas {
  border: 1px solid #ddd;
  cursor: crosshair;
  max-width: 100%;
  display: block;
  margin: 0 auto;
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.hint {
  color: #666;
  font-size: 13px;
  margin-top: 8px;
}

.button-group {
  display: flex;
  gap: 8px;
  margin: 16px 0;
}

.button-group button {
  flex: 1;
}

.matched {
  color: #07c160;
}

.not-matched {
  color: #ff0000;
}
</style>
